<script setup lang="ts">
// One table for the chapter tables and the cost table. Columns from `numericFrom` on
// are right-aligned in mono (Albert Sans has no tabular figures); `total` appends the
// bold summary row, its values filling the last columns. `moneyCol` sets one column,
// and its total, in brass: money reads as money wherever a skimmer lands.
defineProps<{
  columns: string[]
  rows: string[][]
  // Optional muted second line under each row's first cell.
  subs?: (string | undefined)[]
  numericFrom?: number
  moneyCol?: number
  // Fits a narrow screen without sideways scrolling.
  compact?: boolean
  nowrapFirst?: boolean
  total?: { label: string; values: string[] }
}>()
</script>

<template>
  <div>
    <p v-if="columns.length >= 3 && !compact" class="sm:hidden mb-1.5 text-xs text-surface-400">Swipe to see more &rarr;</p>
    <div class="overflow-x-auto rounded-card border border-surface-800">
    <table class="w-full text-left text-sm text-surface-300" :class="compact ? '' : 'min-w-[34rem]'">
      <thead class="bg-surface-900 text-surface-100">
        <tr>
          <th
            v-for="(col, cIdx) in columns"
            :key="col"
            scope="col"
            class="px-3 py-2 font-semibold"
            :class="{
              'text-right whitespace-nowrap': numericFrom !== undefined && cIdx >= numericFrom,
              'whitespace-nowrap': nowrapFirst && cIdx === 0,
            }"
          >{{ col }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rIdx) in rows" :key="rIdx" class="border-t border-surface-800">
          <td
            v-for="(cell, cellIdx) in row"
            :key="cellIdx"
            class="px-3 py-2 align-top"
            :class="{
              'text-right font-mono whitespace-nowrap': numericFrom !== undefined && cellIdx >= numericFrom,
              'text-brass-400': cellIdx === moneyCol,
              'whitespace-nowrap': nowrapFirst && cellIdx === 0,
            }"
          >{{ cell }}<span v-if="cellIdx === 0 && subs?.[rIdx]" class="block text-sm text-surface-400">{{ subs[rIdx] }}</span></td>
        </tr>
        <tr v-if="total" class="border-t border-surface-700 font-bold text-surface-50">
          <td class="px-3 py-2" :colspan="columns.length - total.values.length">{{ total.label }}</td>
          <td
            v-for="(value, vIdx) in total.values"
            :key="vIdx"
            class="px-3 py-2 text-right font-mono whitespace-nowrap"
            :class="{ 'text-brass-400': columns.length - total.values.length + vIdx === moneyCol }"
          >{{ value }}</td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>
</template>
